import React, { createContext, useContext, useState, useCallback } from 'react';
import { CinemaTheme } from '../utils/cinemaThemes';

const STORAGE_KEY = 'cinemaTheme';
const SETTINGS_KEY = (theme: CinemaTheme, key: string) => `cinemaTheme_${theme}_${key}`;

export interface CinemaThemeSettings {
  /** 0–100. At 50 (default) = balanced intensity; 0 = invisible; 100 = maximum. */
  effect: number;
  animated: boolean;
}

const DEFAULT_SETTINGS: CinemaThemeSettings = { effect: 50, animated: true };

function loadSettings(theme: CinemaTheme): CinemaThemeSettings {
  try {
    const effect = localStorage.getItem(SETTINGS_KEY(theme, 'effect'));
    const animated = localStorage.getItem(SETTINGS_KEY(theme, 'animated'));
    return {
      effect: effect !== null ? Number(effect) : DEFAULT_SETTINGS.effect,
      animated: animated !== null ? animated === 'true' : DEFAULT_SETTINGS.animated,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function persistSetting(theme: CinemaTheme, key: keyof CinemaThemeSettings, value: number | boolean) {
  try { localStorage.setItem(SETTINGS_KEY(theme, key), String(value)); } catch { /* noop */ }
}

interface CinemaThemeContextType {
  cinemaTheme: CinemaTheme;
  setCinemaTheme: (theme: CinemaTheme) => void;
  /** True once the user has explicitly picked any theme (including the default).
   *  Distinguishes "never opened settings" from "chose lumiere on purpose". */
  hasChosenTheme: boolean;
  /** Per-theme settings (effect intensity + animation toggle). */
  themeSettings: CinemaThemeSettings;
  setThemeSetting: <K extends keyof CinemaThemeSettings>(key: K, value: CinemaThemeSettings[K]) => void;
}

const CinemaThemeContext = createContext<CinemaThemeContextType>({
  cinemaTheme: 'none',
  setCinemaTheme: () => {},
  hasChosenTheme: false,
  themeSettings: { ...DEFAULT_SETTINGS },
  setThemeSetting: () => {},
});

export const useCinemaTheme = () => useContext(CinemaThemeContext);

export const CinemaThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cinemaTheme, setCinemaThemeState] = useState<CinemaTheme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored as CinemaTheme) || 'lumiere';
  });

  // True as soon as the key exists in localStorage (set by setCinemaTheme below).
  const [hasChosenTheme, setHasChosenTheme] = useState<boolean>(
    () => localStorage.getItem(STORAGE_KEY) !== null,
  );

  const [themeSettings, setThemeSettingsState] = useState<CinemaThemeSettings>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const active = (stored as CinemaTheme) || 'lumiere';
    return loadSettings(active);
  });

  const setCinemaTheme = useCallback((theme: CinemaTheme) => {
    setCinemaThemeState(theme);
    localStorage.setItem(STORAGE_KEY, theme);
    setHasChosenTheme(true);
    // Load saved settings for the newly selected theme
    setThemeSettingsState(loadSettings(theme));
  }, []);

  const setThemeSetting = useCallback(<K extends keyof CinemaThemeSettings>(
    key: K,
    value: CinemaThemeSettings[K],
  ) => {
    setThemeSettingsState((prev) => {
      const next = { ...prev, [key]: value };
      // Read current theme from localStorage so this closure doesn't need a dep
      const current = (localStorage.getItem(STORAGE_KEY) as CinemaTheme) || 'lumiere';
      persistSetting(current, key, value as number | boolean);
      return next;
    });
  }, []);

  return (
    <CinemaThemeContext.Provider value={{ cinemaTheme, setCinemaTheme, hasChosenTheme, themeSettings, setThemeSetting }}>
      {children}
    </CinemaThemeContext.Provider>
  );
};
